/**
 * The composer's `/` menu, as rules.
 *
 * WHAT IT IS. Typing `/` as the FIRST character of a message opens a short list of housekeeping
 * commands — new chat, stop, status, cancel the open task. Each one calls an action the page
 * already has; none is sent to the agent as a message and none costs a model call.
 *
 * WHY A MODULE AND NOT INLINE IN THE ROUTE. Every decision the menu makes is a rule that must not
 * drift, and `MultiAgentRoute` cannot be rendered by the node suite: when the menu is open at all,
 * which commands may run in the current session state, what a destructive one says before it runs,
 * and which keys it takes from the textarea. They are pure functions of the composer text and the
 * session state so each has a test.
 *
 * THE ONE EXCEPTION TO "THE CLIENT NEVER READS WHAT THE USER TYPED" (`laneV2/askRouting.ts`). That
 * rule is about MEANING: the client must not guess intent from words. This reads SYNTAX only — a
 * leading slash followed by a name from the fixed list below — and it runs nothing the reader did
 * not pick from the list. Text that starts with `/` and matches no command (a pasted
 * `/services/apexrest/...` path) leaves the menu closed and is sent verbatim, exactly as before.
 */
import { PROMPT_LIBRARY } from "@/constants/multi-agent/promptLibrary.constants";
import { transitionWords } from "./laneV2/activity";
import { pendingAsks } from "./laneV2/reduce";
import { TERMINAL_TASK_STATES, type AskView, type TaskSummary, type UsageTotals } from "./laneV2/types";
import { formatTokens } from "./usageMeter";

/** The commands that turn a name into a full request (`shortcutPrompt`). */
export type ShortcutId = "explain" | "test" | "review";

export type SlashCommandId =
  | ShortcutId
  | "prompts"
  | "new"
  | "stop"
  | "status"
  | "usage"
  | "compact"
  | "history"
  | "undo"
  | "cleanup";

export interface SlashCommand {
  id: SlashCommandId;
  /** What the reader types, slash included. */
  name: string;
  description: string;
  /** Changes something that is not undone by typing again: asks for a second Enter first. */
  confirm: boolean;
  /** Something is typed after it (`/explain AccountService`, `/prompts test`): picking it adds a space, not a run. */
  takesArg: boolean;
}

/**
 * The list, in the order the menu shows it. The work shortcuts lead: they are the ones that save
 * writing a request, where the rest save a click.
 */
export const SLASH_COMMANDS: readonly SlashCommand[] = [
  { id: "explain", name: "/explain", description: "Explain a class, object or field", confirm: false, takesArg: true },
  { id: "test", name: "/test", description: "Write an Apex test class for a class", confirm: false, takesArg: true },
  { id: "review", name: "/review", description: "Review a class for bugs and security problems", confirm: false, takesArg: true },
  { id: "prompts", name: "/prompts", description: "Pick a starter prompt", confirm: false, takesArg: true },
  { id: "new", name: "/new", description: "Start a new conversation", confirm: false, takesArg: false },
  { id: "stop", name: "/stop", description: "Stop the turn that is running", confirm: false, takesArg: false },
  { id: "status", name: "/status", description: "Show what is running and what is waiting for you", confirm: false, takesArg: false },
  { id: "usage", name: "/usage", description: "Show what this conversation has used", confirm: false, takesArg: false },
  // `/compact` runs as it stands; anything typed after it is what the summary must keep (`parseSlash`).
  { id: "compact", name: "/compact", description: "Summarise the conversation so far and continue from the summary", confirm: true, takesArg: false },
  { id: "history", name: "/history", description: "Open your past sessions", confirm: false, takesArg: false },
  { id: "undo", name: "/undo", description: "Open a revert of the last applied change", confirm: true, takesArg: false },
  { id: "cleanup", name: "/cleanup", description: "Cancel the open task and its pending questions", confirm: true, takesArg: false },
];

/**
 * The request each work shortcut writes around the name the reader typed.
 *
 * FIXED SENTENCES, NOT GUESSES. The name goes in verbatim; nothing here reads what kind of thing it
 * is. The result fills the composer and is NOT sent — the reader sees the exact request, can edit
 * it, and sends it themselves, so a shortcut can never send something they did not read.
 */
const SHORTCUT_TEMPLATES: Record<ShortcutId, (name: string) => string> = {
  explain: (name) => `Explain what ${name} does and everywhere it is referenced in this org.`,
  test: (name) => `Write an Apex test class for ${name} that covers its main paths, and tell me what it does not cover.`,
  review: (name) =>
    `Review ${name} for bugs, security problems (CRUD and field-level security, SOQL injection) and ` +
    "bulk-safety problems. List each finding with where it is.",
};

function isShortcut(id: string): id is ShortcutId {
  return Object.prototype.hasOwnProperty.call(SHORTCUT_TEMPLATES, id);
}

export function shortcutPrompt(id: ShortcutId, name: string): string {
  return SHORTCUT_TEMPLATES[id](name);
}

/** The session facts the menu reads. Machine state only — never the conversation's words. */
export interface SlashContext {
  laneV2: boolean;
  /** A client-owned session id nothing has been sent on yet. */
  draft: boolean;
  sessionId: string | null;
  /** A turn is running, or a send is still in flight. */
  inFlight: boolean;
  tasks: readonly TaskSummary[];
  asks: readonly AskView[];
  /** The session's running total (R1 `usage_total`); `null` before the first read. */
  usage: UsageTotals | null;
  /** This server has the compact routes and the session can be compacted (`useSessionCompaction`). */
  compactAvailable: boolean;
  /** Each server turn's own usage (R5 `usage`), oldest first; `null` for a turn that spent nothing. */
  turnUsage: readonly (UsageTotals | null)[];
  /** How many turns the live compaction covers (its boundary `turn_seq`); 0 when there is none. */
  compactedTurns: number;
}

export type SlashParse =
  | { mode: "commands"; query: string }
  | { mode: "prompts"; query: string }
  | { mode: "shortcut"; id: ShortcutId; arg: string }
  | { mode: "compact"; focus: string }
  | null;

const WITH_ARG = /^\/([a-z]+)\s+(.*)$/i;
const ONE_TOKEN = /^\/(\S*)$/;

/**
 * What the composer text asks the menu for, or `null` when the menu has no business being open.
 *
 * Open only for a single line whose first character is `/`. One token after the slash is a command
 * query. A space after a command that TAKES something keeps the menu open: `/prompts` switches to
 * the prompt list and the rest filters it; a work shortcut reads the rest as the name, case kept.
 * Anything else with a space in it is a sentence, so the menu stays out of the way.
 */
export function parseSlash(input: string): SlashParse {
  if (!input.startsWith("/") || input.includes("\n")) return null;
  const withArg = WITH_ARG.exec(input);
  if (withArg) {
    const id = withArg[1].toLowerCase();
    const rest = withArg[2].trim().replace(/\s+/g, " ");
    if (id === "prompts") return { mode: "prompts", query: rest.toLowerCase() };
    // Only with something after it: `/compact` alone is a plain command and is matched like any other.
    if (id === "compact") return rest ? { mode: "compact", focus: rest } : null;
    return isShortcut(id) ? { mode: "shortcut", id, arg: rest } : null;
  }
  const token = ONE_TOKEN.exec(input);
  return token ? { mode: "commands", query: token[1].toLowerCase() } : null;
}

export function matchCommands(query: string): SlashCommand[] {
  const needle = query.toLowerCase();
  return SLASH_COMMANDS.filter((c) => c.id.startsWith(needle));
}

/**
 * A command this server does not have is not listed at all, where one that merely cannot run NOW is
 * listed greyed with its reason: "greyed" promises the command exists.
 */
function offered(id: SlashCommandId, ctx: SlashContext): boolean {
  // `/undo` reverts an applied change; forward tasks rest at `validated` and never reach `applied`, so it is listed only
  // for a session that still holds a legacy applied row.
  if (id === "undo") return !!lastAppliedTask(ctx.tasks);
  return id !== "compact" || (ctx.laneV2 && ctx.compactAvailable);
}

/** The session's open task. The server allows at most one (`tasks_one_open_per_session`). */
export function openTask(tasks: readonly TaskSummary[]): TaskSummary | null {
  return tasks.find((t) => !TERMINAL_TASK_STATES.has(t.state)) ?? null;
}

/** The task whose change reached the org most recently — the only kind a revert can be opened for. */
export function lastAppliedTask(tasks: readonly TaskSummary[]): TaskSummary | null {
  const applied = tasks.filter((t) => t.state === "applied");
  if (applied.length === 0) return null;
  return applied.reduce((newest, t) => ((t.updated_at || "") > (newest.updated_at || "") ? t : newest));
}

export interface Availability {
  available: boolean;
  /** Why not, in words; `null` when it can run. */
  reason: string | null;
}

const CAN_RUN: Availability = { available: true, reason: null };
const no = (reason: string): Availability => ({ available: false, reason });

/**
 * Whether a command can run now. A command that cannot is still listed, greyed, with this reason —
 * a missing row would leave the reader guessing whether the command exists.
 *
 * These mirror what the server would answer, so a refusal is read here instead of drawn from a
 * failed request: cancel is refused while
 * the task is applying to the org, and a revert opens only from an applied task with no other task
 * open.
 */
export function availabilityOf(id: SlashCommandId, ctx: SlashContext): Availability {
  // These only touch the page or fill the message, so they work in any chat.
  if (id === "new" || id === "history" || id === "prompts" || isShortcut(id)) return CAN_RUN;
  if (!ctx.laneV2) return no("Not available in this chat.");
  const open = openTask(ctx.tasks);
  switch (id) {
    case "stop":
      return ctx.inFlight ? CAN_RUN : no("Nothing is running.");
    case "status":
    case "usage":
      return CAN_RUN;
    case "compact":
      if (!ctx.compactAvailable) return no("Not available in this chat.");
      return ctx.inFlight ? no("Wait for the running turn to finish.") : CAN_RUN;
    case "undo":
      if (open) return no("Finish or cancel the open task first.");
      return lastAppliedTask(ctx.tasks) ? CAN_RUN : no("No applied change to undo.");
    case "cleanup":
      if (!open) return no("No open task to cancel.");
      return open.state === "applying"
        ? no("The task is applying changes to your org and can't be cancelled until that finishes.")
        : CAN_RUN;
  }
}

const AGAIN = "Press Enter again to confirm.";

/** What the second Enter will do, for the two commands that ask for one; `null` for the rest. */
export function confirmCopy(id: SlashCommandId, ctx: SlashContext): string | null {
  if (id === "undo") {
    return `Open a revert of the last applied change? You will be asked to confirm before anything is deployed. ${AGAIN}`;
  }
  if (id === "compact") {
    // What changes, who still sees what, and that it costs a call: the three things a reader would ask.
    return (
      "Summarise the conversation so far and continue from the summary? Weave will carry the summary in place of the " +
      `earlier messages; you can still read them all. This uses one model call. ${AGAIN}`
    );
  }
  if (id !== "cleanup") return null;
  const open = openTask(ctx.tasks);
  const words = open ? ` (${transitionWords(open.state)})` : "";
  const waiting = open ? pendingAsks(ctx.asks).filter((a) => a.task_id === open.task_id).length : 0;
  const questions = waiting ? ` and withdraw its ${waiting} pending question${waiting === 1 ? "" : "s"}` : "";
  // The limits are part of the question: a cancel does not interrupt work already in a model call,
  // and it undoes nothing in the org. Saying so after the fact would be too late.
  return (
    `Cancel the open task${words}${questions}? Work already running finishes and is discarded. ` +
    `Nothing already applied to your org is undone. ${AGAIN}`
  );
}

/** `/status`, as sentences: the turn, the open task, the questions waiting. */
export function statusLines(ctx: SlashContext): string[] {
  if (!ctx.laneV2) return ["Status is not available in this chat."];
  const open = openTask(ctx.tasks);
  const waiting = pendingAsks(ctx.asks).length;
  return [
    ctx.inFlight ? "A turn is running." : "No turn is running.",
    open ? `Open task: ${transitionWords(open.state)}.` : "No open task.",
    waiting === 0
      ? "No questions are waiting."
      : waiting === 1
        ? "1 question is waiting for you."
        : `${waiting} questions are waiting for you.`,
  ];
}

/** How many turns the brief carries in full (MSA `SESSION_RECENT_TURNS`): past it, earlier messages fall out of view. */
export const TURNS_CARRIED_IN_FULL = 8;

/**
 * A cost to the precision it has. The usage meter rounds anything under a cent to "<$0.01", which is right for a chip
 * and wrong for the one command whose job is the number: most Weave turns cost a fraction of a cent.
 */
export function preciseCost(usd: number): string {
  if (!(usd > 0)) return "$0.00";
  if (usd < 0.0001) return "<$0.0001";
  return usd < 0.01 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(2)}`;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/**
 * `/usage`, as lines: the conversation's total, the last message's own share, and a pointer to `/compact` when the chat
 * has outgrown what Weave carries in full.
 *
 * EVERY NUMBER IS THE SERVER'S. The total is R1 `usage_total` and the last message's is that turn's R5 `usage`; nothing
 * is estimated here. Two limits the wording respects: "tokens in" counts everything sent to the model, cached re-reads
 * included (the server does not split them out), and the total covers the conversation's turns, not the model call a
 * `/compact` makes.
 */
export function usageLines(ctx: SlashContext): string[] {
  if (!ctx.laneV2) return ["Usage is not available in this chat."];
  const u = ctx.usage;
  if (!u || (u.tokens_in + u.tokens_out === 0 && u.cost_usd === 0 && u.tool_calls === 0)) {
    return ["This conversation has not used anything yet."];
  }
  const messages = ctx.turnUsage.length;
  const lines = [
    `This conversation: ${messages ? `${plural(messages, "message")}, ` : ""}${formatTokens(u.tokens_in)} tokens in, ` +
      `${formatTokens(u.tokens_out)} out, ${plural(u.tool_calls, "tool call")}, ${preciseCost(u.cost_usd)}.`,
  ];
  const last = [...ctx.turnUsage].reverse().find((t) => t !== null) ?? null;
  if (last && messages > 1) {
    lines.push(`Last message: ${formatTokens(last.tokens_in)} tokens in, ${formatTokens(last.tokens_out)} out, ${preciseCost(last.cost_usd)}.`);
  }
  const sinceCompact = messages - ctx.compactedTurns;
  if (ctx.compactAvailable && sinceCompact > TURNS_CARRIED_IN_FULL) {
    lines.push(
      `Weave carries the last ${TURNS_CARRIED_IN_FULL} messages in full; this chat has ${sinceCompact}` +
        `${ctx.compactedTurns ? " since its last compact" : ""}. /compact keeps the earlier ones as a summary.`,
    );
  }
  return lines;
}

export interface PromptMatch {
  key: string;
  category: string;
  title: string;
  prompt: string;
}

/** The prompt library as one flat list in its own order, filtered by any part of the title. */
export function matchPrompts(query: string): PromptMatch[] {
  const needle = query.trim().toLowerCase();
  return PROMPT_LIBRARY.flatMap((category) =>
    category.prompts
      .filter((p) => p.title.toLowerCase().includes(needle))
      .map((p) => ({ key: `${category.id}:${p.title}`, category: category.label, title: p.title, prompt: p.prompt })),
  );
}

/** One row of the menu. */
export interface SlashMenuItem {
  key: string;
  label: string;
  description: string;
  disabled: boolean;
  reason: string | null;
}

/** The rows for the current text and session. Empty means the menu is closed. */
export function menuItems(parse: SlashParse, ctx: SlashContext): SlashMenuItem[] {
  if (!parse) return [];
  if (parse.mode === "shortcut") {
    const name = `/${parse.id}`;
    // One row either way: what Enter will put in the composer, or what is still missing.
    return parse.arg
      ? [{ key: parse.id, label: `${name} ${parse.arg}`, description: shortcutPrompt(parse.id, parse.arg), disabled: false, reason: null }]
      : [{ key: parse.id, label: name, description: "", disabled: true, reason: `Type a name after it, for example ${name} AccountService.` }];
  }
  if (parse.mode === "compact") {
    if (!offered("compact", ctx)) return [];
    const { available, reason } = availabilityOf("compact", ctx);
    return [
      { key: "compact", label: `/compact ${parse.focus}`, description: "Summarise, making sure to keep this", disabled: !available, reason },
    ];
  }
  if (parse.mode === "prompts") {
    return matchPrompts(parse.query).map((p) => ({
      key: p.key,
      label: p.title,
      description: p.category,
      disabled: false,
      reason: null,
    }));
  }
  return matchCommands(parse.query)
    .filter((c) => offered(c.id, ctx))
    .map((c) => {
    const { available, reason } = availabilityOf(c.id, ctx);
    return { key: c.id, label: c.name, description: c.description, disabled: !available, reason };
  });
}

/**
 * What a command said when it ran, and WHERE: the line under the composer.
 *
 * The page that shows it outlives any one conversation (the route stays mounted while the reader opens another session,
 * starts a new one from the header, or picks one in the sidebar), so a bare string kept in the route was still on screen
 * in the next conversation: "No open task." from chat A, read as a fact about chat B. A note therefore carries the
 * conversation it was produced in and is shown only there. That also settles the late answer: `/compact` replies seconds
 * after it was asked, and its line is recorded under the conversation it STARTED in, never the one now on screen.
 */
export interface SlashNote {
  conversation: string;
  text: string;
}

/** The conversation's name for that purpose: the lane and its session id (the two lanes keep separate ids). */
export function conversationOf(laneV2: boolean, v2SessionId: string | null, legacySessionId: string | null): string {
  return laneV2 ? `v2:${v2SessionId ?? ""}` : `v1:${legacySessionId ?? ""}`;
}

/** The note's text if it belongs to the conversation on screen, else nothing. */
export function noteFor(note: SlashNote | null, conversation: string): string | null {
  return note && note.conversation === conversation ? note.text : null;
}

export type SlashKeyIntent = "up" | "down" | "run" | "close" | null;

/**
 * What a key pressed in the textarea means to the menu; `null` means "not mine", and the textarea's
 * own handling (Enter sends) runs untouched.
 *
 * Nothing is taken while the menu is closed, and nothing while an input method is composing: there
 * Enter commits the composition and must not run a command.
 */
export function slashKeyIntent(key: string, o: { open: boolean; shift: boolean; composing: boolean }): SlashKeyIntent {
  if (!o.open || o.composing) return null;
  switch (key) {
    case "ArrowUp":
      return "up";
    case "ArrowDown":
      return "down";
    case "Escape":
      return "close";
    case "Tab":
      return "run";
    case "Enter":
      return o.shift ? null : "run";
    default:
      return null;
  }
}

/** The highlight after an arrow key, wrapping at both ends. */
export function moveIndex(index: number, count: number, dir: "up" | "down"): number {
  if (count <= 0) return 0;
  return (index + (dir === "down" ? 1 : -1) + count) % count;
}
